import { createContext, FC, useContext, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Box, Breadcrumbs, Chip } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import BrandLogo from '../SignIn/BrandLogo';

// shared with the sidebar's top row so their bottom borders line up
export const HEADER_HEIGHT = 64;

// Lets a page name the last breadcrumb (e.g. the account being viewed)
export const PageTitleContext = createContext<(title: string) => void>(() => undefined);

export const usePageTitle = (title?: string) => {
  const setTitle = useContext(PageTitleContext);
  useEffect(() => {
    if (title) setTitle(title);
    return () => setTitle('');
  }, [title, setTitle]);
};

const SECTIONS: Record<string, string> = {
  accounts: 'Accounts',
  plans: 'Plans',
  requests: 'Requests',
  notifications: 'Notifications',
  team: 'Team',
};

interface IPlatformHeaderProps {
  title: string;
  admin?: { name: string; role: string };
}

const PlatformHeader: FC<IPlatformHeaderProps> = ({ title, admin }) => {
  const { pathname } = useLocation();
  const [section, id] = pathname.replace(/^\/platform\/?/, '').split('/').filter(Boolean);

  const crumbs: { label: string; to?: string }[] = [{ label: 'Dashboard', to: '/platform' }];
  if (section && SECTIONS[section]) crumbs.push({ label: SECTIONS[section], to: `/platform/${section}` });
  if (id) crumbs.push({ label: title || 'Loading…' });
  // the current page is never a link
  delete crumbs[crumbs.length - 1].to;

  return (
    <Box sx={{ position: 'sticky', top: 0, zIndex: 10, height: HEADER_HEIGHT, boxSizing: 'border-box', px: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, bgcolor: '#fff', borderBottom: '1px solid #dcdcdc' }}>
      <Breadcrumbs separator={<NavigateNextIcon fontSize='small' />} sx={{ fontSize: 14, minWidth: 0, '& ol': { flexWrap: 'nowrap' }, '& .MuiBreadcrumbs-separator': { mx: 0.25 } }}>
        {crumbs.map(({ label, to }) => (to
          ? <Link key={label} to={to} style={{ color: '#059669', textDecoration: 'none', whiteSpace: 'nowrap' }}>{label}</Link>
          : <span key={label} style={{ color: '#111827', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>))}
      </Breadcrumbs>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
        {admin && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span style={{ fontSize: 13, color: '#374151' }}>{admin.name}</span>
            <Chip size='small' variant='outlined' color='primary' label={admin.role === 'owner' ? 'Owner' : 'Staff'} sx={{ height: 22, fontSize: 12 }} />
          </Box>
        )}
        <Box sx={{ width: '1px', height: 28, bgcolor: '#dcdcdc' }} />
        <BrandLogo isMobile />
      </Box>
    </Box>
  );
};

export default PlatformHeader;
